import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Plus, Trash2, Wrench, Boxes, ArrowUpCircle, CheckCircle2, ExternalLink, RadioTower } from "lucide-react";
import { LangIcon, CloudIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";

const MODES = [
  { key: "minimum", label: "Minimum version", hint: ">= x.y.z" },
  { key: "pinned", label: "Specific version", hint: "= x.y.z" },
  { key: "compatible", label: "Compatible (~>)", hint: "~> x.y" },
];
const TOOLS = [
  { key: "terraform", label: "Terraform" },
  { key: "bicep", label: "Bicep" },
];
const PROVIDER_CLOUD = { azurerm: "azure", azapi: "azure", azuread: "azure", aws: "aws", google: "gcp", "google-beta": "gcp" };

const CONSTRAINT_OPERATORS = { pinned: "=", compatible: "~>" };

function constraint(spec) {
  if (!spec?.version) return "—";
  return `${CONSTRAINT_OPERATORS[spec.mode] || ">="} ${spec.version}`;
}

function watchSummary(latest, outdated) {
  if (!latest) return "checking upstream releases…";
  if (!outdated) return "everything is on the latest release";
  return `${outdated} upgrade${outdated > 1 ? "s" : ""} available`;
}

function saveLabel(saving, dirty) {
  if (saving) return "Saving…";
  return dirty ? "Save changes" : "Saved";
}

function LatestBadge({ info, testid }) {
  if (!info) return <span className="font-mono-tight text-[10px] text-slate-300" data-testid={testid}>checking…</span>;
  if (!info.latest) return <span className="font-mono-tight text-[10px] text-slate-400" data-testid={testid}>latest unknown</span>;
  const outdated = info.status === "outdated";
  return (
    <a
      href={info.url}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      data-status={info.status}
      title={outdated ? `A newer release is available: ${info.latest}` : "You are on the latest release"}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono-tight text-[10px] transition-colors",
        outdated ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100" : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
      )}
    >
      {outdated ? <ArrowUpCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
      {outdated ? `upgrade available · ${info.latest}` : `latest ${info.latest}`}
      <ExternalLink className="h-2.5 w-2.5 opacity-60" />
    </a>
  );
}

function ModeSelect({ value, onChange, disabled, testid }) {
  return (
    <Select value={value || "minimum"} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid} className="h-9 border-slate-200 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        {MODES.map((m) => <SelectItem key={m.key} value={m.key}>{m.label} <span className="ml-1 font-mono-tight text-[10px] text-slate-400">{m.hint}</span></SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function IacPage() {
  const { user } = useAuth();
  const readOnly = user?.role !== "admin";
  const [iac, setIac] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newProv, setNewProv] = useState("");
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    client.get("/governance/rules").then((r) => { setIac(r.data.iac_versions); setSaved(JSON.stringify(r.data.iac_versions)); });
  }, []);
  useEffect(() => {
    if (saved) client.get("/iac/latest").then((r) => setLatest(r.data)).catch(() => setLatest({ tools: {}, providers: {} }));
  }, [saved]);

  if (!iac) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  const dirty = JSON.stringify(iac) !== saved;
  const outdated = latest ? [...Object.values(latest.tools || {}), ...Object.values(latest.providers || {})].filter((i) => i.status === "outdated").length : 0;

  const setTool = (k, patch) => setIac((d) => ({ ...d, tools: { ...d.tools, [k]: { ...(d.tools?.[k] || {}), ...patch } } }));
  const setProv = (k, patch) => setIac((d) => ({ ...d, providers: { ...d.providers, [k]: { ...(d.providers?.[k] || {}), ...patch } } }));
  const removeProv = (k) => setIac((d) => { const p = { ...d.providers }; delete p[k]; return { ...d, providers: p }; });
  const addProv = () => {
    const name = newProv.trim().toLowerCase();
    if (!name || iac.providers?.[name]) return;
    setProv(name, { source: `hashicorp/${name}`, mode: "pinned", version: "" });
    setNewProv("");
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch("/governance/rules", { iac_versions: iac });
      setIac(data.iac_versions);
      setSaved(JSON.stringify(data.iac_versions));
      toast.success("IaC versions saved — projects are re-evaluated");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-10">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// iac</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">IaC tooling</h1>
          <p className="mt-1 text-sm text-slate-600">Tool and provider versions every project must use. Generated code pins them in <code className="font-mono-tight">versions.tf</code>; projects and CI checks that drift are flagged as "IaC compliance" failed.</p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 font-mono-tight text-[11px] text-slate-600" data-testid="version-watch-summary">
            <RadioTower className="h-3.5 w-3.5 text-slate-400" />
            Version watch · {watchSummary(latest, outdated)}
          </p>
        </div>
        {!readOnly && (
          <Button onClick={save} disabled={!dirty || saving} className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40" data-testid="save-iac">
            <Save className="mr-2 h-4 w-4" /> {saveLabel(saving, dirty)}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="iac-tools">
          <div className="mb-5 flex items-center gap-2"><Wrench className="h-4 w-4 text-slate-400" /><h2 className="font-display text-lg font-semibold">IaC tools</h2></div>
          <div className="space-y-4">
            {TOOLS.map(({ key, label }) => {
              const t = iac.tools?.[key] || {};
              return (
                <div key={key} className="rounded-md border border-slate-200 p-4" data-testid={`tool-${key}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-display text-sm font-semibold"><LangIcon language={key} className="h-4 w-4" /> {label}</span>
                    <div className="flex items-center gap-2">
                      <LatestBadge info={latest?.tools?.[key]} testid={`tool-latest-${key}`} />
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono-tight text-[11px] text-slate-700" data-testid={`tool-constraint-${key}`}>{constraint(t)}</span>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-[1fr_140px] gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Constraint</Label>
                      <div className="mt-1"><ModeSelect value={t.mode} onChange={(v) => setTool(key, { mode: v })} disabled={readOnly} testid={`tool-mode-${key}`} /></div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Version</Label>
                      <Input readOnly={readOnly} data-testid={`tool-version-${key}`} className="mt-1 h-9 border-slate-200 font-mono-tight text-xs" placeholder="1.9.0" value={t.version || ""} onChange={(e) => setTool(key, { version: e.target.value })} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="iac-providers">
          <div className="mb-5 flex items-center gap-2"><Boxes className="h-4 w-4 text-slate-400" /><h2 className="font-display text-lg font-semibold">Terraform providers</h2></div>
          <div className="space-y-3">
            {Object.entries(iac.providers || {}).map(([name, spec]) => (
              <div key={name} className="rounded-md border border-slate-200 p-4" data-testid={`provider-${name}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-display text-sm font-semibold">
                    {PROVIDER_CLOUD[name] && <CloudIcon cloud={PROVIDER_CLOUD[name]} className="h-4 w-4" />} {name}
                    <span className="font-mono-tight text-[10px] font-normal text-slate-400">{spec.source || `hashicorp/${name}`}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <LatestBadge info={latest?.providers?.[name]} testid={`provider-latest-${name}`} />
                    <span className={cn("rounded-full px-2 py-0.5 font-mono-tight text-[11px]", spec.version ? "bg-slate-100 text-slate-700" : "bg-amber-50 text-amber-700")} data-testid={`provider-constraint-${name}`}>{constraint(spec)}</span>
                    {!readOnly && (
                      <button type="button" onClick={() => removeProv(name)} className="rounded p-1 text-slate-400 hover:text-rose-600" aria-label={`Remove ${name}`} data-testid={`provider-remove-${name}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-[1fr_1fr_140px] gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">Source</Label>
                    <Input readOnly={readOnly} data-testid={`provider-source-${name}`} className="mt-1 h-9 border-slate-200 font-mono-tight text-xs" value={spec.source || ""} onChange={(e) => setProv(name, { source: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">Constraint</Label>
                    <div className="mt-1"><ModeSelect value={spec.mode} onChange={(v) => setProv(name, { mode: v })} disabled={readOnly} testid={`provider-mode-${name}`} /></div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">Version</Label>
                    <Input readOnly={readOnly} data-testid={`provider-version-${name}`} className="mt-1 h-9 border-slate-200 font-mono-tight text-xs" placeholder="4.10.0" value={spec.version || ""} onChange={(e) => setProv(name, { version: e.target.value })} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {!readOnly && (
            <form onSubmit={(e) => { e.preventDefault(); addProv(); }} className="mt-4 flex items-center gap-2">
              <Input data-testid="provider-new-name" className="h-9 border-slate-200 font-mono-tight text-xs" placeholder="azapi, azuread, google-beta, random…" value={newProv} onChange={(e) => setNewProv(e.target.value)} />
              <Button type="submit" variant="outline" className="h-9 border-slate-200" data-testid="provider-add"><Plus className="mr-1.5 h-3.5 w-3.5" /> Add provider</Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
